import { useDisclosure, useToast } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";

import { BCADocumentFile, BCADocumentFileType } from "@bcasmart/bca-client-signature/BCADocuments";
import { checkDocumentPermissions, extractAndCheckDSSObject, extractAndVerifySignatures } from "@bcasmart/bca-client-signature/BCASignatureEngine";
import { BCAObjectDSS, BCAPDFSignedData, BCAReturnCode } from "@bcasmart/bca-client-signature/BCAQSCDTypes";
import { CloudSubscriptionDto, DocumentDto, SessionDto, SignatureDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { SessionStatus, SessionSystemType, SignaturePhase, SignatureStatus, SignatureType } from "@bcasmart/doxecure-commons/doxecureModelTypes";
import { DocumentSignaturePosition, DriveItemStructure, SignatureMode } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $map } from "foundation-ts/array";
import { $count, $email, $isnumber, $isstring, $length, $ok, $UUID, $value } from "foundation-ts/commons";
import { $uuid } from "foundation-ts/crypto";
import { $arrayBufferFromDataLike, $encodeBase64 } from "foundation-ts/data";
import { $timeBetweenDates } from "foundation-ts/date";
import { $newext, $withoutext } from "foundation-ts/fs";
import { TSDate, TSDay } from "foundation-ts/tsdate";
import { TSError } from "foundation-ts/tserrors";
import { TSInterval } from "foundation-ts/tsinterval";
import { Nullable, TSDataLike, uint32, uint8, UUID } from "foundation-ts/types";
import { $inspect } from "foundation-ts/utils";

import { PageShellWithThreeColumns } from "@components/PageShellWithThreeColumns/PageShellWithThreeColumns";
import { WaitingModal } from "@components/Modals/WaitingModal";
import { config } from "@/config/config";
import { DocumentPanel } from "@containers/signature/DocumentPanel";
import { DriveExplorerPanel } from "@containers/signature/DriveExplorerPanel";
import { PersonItem, PersonPanel } from "@containers/signature/PersonPanel";
import { SessionCreationPanel } from "@containers/signature/SessionCreationPanel";
import { TemplateSessionCreationPanel } from "@containers/templateSessions/TemplateSessionCreationPanel";
import { useAuthContext } from "@hooks/useAuthContext";
import useCreateOrUpdateDraftSession from "@hooks/useCreateOrUpdateDraftSession";
import useCreateOrUpdateTemplateSession from "@hooks/useCreateOrUpdateTemplateSession";
import useSession from "@hooks/useSession";
import { DocumentContentID } from "@hooks/hooksConstants";
import { publishSession } from "@services/session.service";
import { g1call } from "@services/apiClient";
import { downloadDriveFile } from "@services/drive.service";
import { blobToURLData, fileNameString, okOrEmptyCard, optlog, trueOrEmptyCard } from "@utils/functions";
import { NeededPersonRole } from "@utils/TypesAndConstants";
import { localformat, localtrans } from "@utils/LocalTranslations";
import { ToastInfo, defaultErrorToast, defaultWarningToast, defaultSuccessToast, toastInfo } from "@utils/toast";
import { LoadingFailedType } from "@utils/viewPDFTypes";
import { BCABrowserDocumentFile } from "@/utils/BCABrowserDocumentFile";

interface NewSessionViewProps {
    systemType: SessionSystemType ;
}

export const NewSessionView = (props: NewSessionViewProps) => {
    const { systemType } = props ;
    const { isUserAuthenticated, connectedUser } = useAuthContext();
    const params = useParams();
    const { data: initialSession, isError, isLoading } = useSession($UUID(params.sessionId), connectedUser?.apid);
    const { mutateAsync: updateOrCreateDraftSession } = useCreateOrUpdateDraftSession(connectedUser?.apid);
    const { mutateAsync: updateOrCreateTemplateSession } = useCreateOrUpdateTemplateSession(connectedUser?.apid);
    const [signatureMode, setSignatureMode] = useState<SignatureMode>(SignatureMode.Visible);
    const [optionInvisibleSwitchChecked, setOptionInvisibleSwitchChecked] = useState(false);
    const [optionAddApproversSwitchChecked, setOptionAddApproversSwitchChecked] = useState(false);
    const [optionOneToManyModeSwitchChecked, setOptionsOneToManyModeSwitchChecked] = useState(false);
    const [rightPaneContent, setRightPaneContent] = useState<RightPanelType | undefined>(undefined);
    const [document, setDocument] = useState<NewDocumentDto | undefined>(undefined);
    const [pdfSignatures, setPdfSignatures] = useState<SignatureDto[]>([]) ;
    const [drive, setDrive] = useState<CloudSubscriptionDto | undefined>(undefined);
    const [personRole, setPersonRole] = useState<NeededPersonRole | undefined>(undefined);
    const [canAddConnectedUser, setCanAddConnectedUser] = useState(false) ;
    const [signature, setSignature] = useState<SignatureDto | undefined>(undefined) ;
    const [openFailModal, setOpenFailModal] = useState(false);
    // const [confirmModal, setConfirmModal] = useState(DefaultAsyncConfirmModalState);
    const [workedSession, setWorkedSession] = useState<SessionDto>();
    const [blacklist, setBlacklist] = useState<Set<UUID>>() ;
    const [modalText, setModalText] = useState('Upload en cours...') ;
    const [uploadedDocumentsSize, setUploadedDocumentsSize] = useState<uint32>(10*1024 as uint32) ;
    // const [showToastQualifiedSigner, setShowToastQualifiedSigner] = useState(true) ;
    // const [showToastSameAsExpeditor, setShowToastSameAsExpeditor] = useState(true) ;
    const signersPositionMap = useRef(new Map<UUID, DocumentSignaturePosition[]>());
    const viewerRef = useRef<HTMLIFrameElement>(null);
    const signaturesCache = useRef<Nullable<SignatureDto[]>>(null); // Used signatures cache when switching between classic and solo mode (avoid to loose ranks)
    const queryClient = useQueryClient();
    const toast = useToast();
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure() ;
    const workedSessionRef = useRef<SessionDto|null>(null); // Ref used by functions called in 'message' eventListener because workedSession is undefined when subscribing to this event

    optlog(`+++ New ${systemType === SessionSystemType.Template ? "template" : ""} session view initialized with profile ID '${connectedUser?.apid}'${config.app.officeDocuments ? " (can upload office documents)" : ""}`) ;

    const clearRightPane = () => {
        setRightPaneContent(undefined);
        setDocument(undefined);
        setPersonRole(undefined);
        setDrive(undefined);
        setBlacklist(undefined);
        setCanAddConnectedUser(false) ;
        setPdfSignatures([]) ;
    }

    // ==================== global actions management ========================================================================================
    const _validateAndSendSessionData = async (checkPlacedSigners: boolean = false): Promise<SessionDto> => {
        let signatureCounts = $count(workedSessionRef.current?.signatures);
        if (optionOneToManyModeSwitchChecked) {
            workedSessionRef.current!.systemType = SessionSystemType.OneToMany;
            signatureCounts = 1; // Only one signer per document
        } else {
            workedSessionRef.current!.systemType = SessionSystemType.Standard;
        }

        if (optionInvisibleSwitchChecked) {
            signersPositionMap.current.clear();
        } else if (checkPlacedSigners) {
            const totalPlacedSigners = Array.from(signersPositionMap.current.values()).reduce((total, signersPosition) => total + $count(signersPosition), 0);
            if (totalPlacedSigners < ($count(workedSessionRef.current?.documents) * signatureCounts)) {
                TSError.throw(localtrans("NewSessionView.MissingPlacedSigners"));
            }
        }

        optlog("save signers position => ", signersPositionMap.current);
        for (const [docId, signersPosition] of signersPositionMap.current.entries()) {
            let documentToUpdate = workedSessionRef.current?.documents?.find(doc => doc.apid === docId || doc.localUUID === docId);
            if (!$ok(documentToUpdate)) { continue; }
            if ($count(signersPosition) > 0) {
                documentToUpdate!.visibleSignatures = signersPosition;
            }
        }

        if (!optionAddApproversSwitchChecked && $count(workedSessionRef.current?.approvals) !== 0) { // Possible if user activate this option, select one or more approvers and deactivate the option
            workedSessionRef.current!.approvals = [];
        }

        // // It's possible that sender has not the real phase when it's placed at last position (in particular when user switch more times between onetomany and normal modes)
        // const expeditorIndex = workedSessionRef.current?.signatures?.findIndex(s => s.signer.apid === workedSessionRef.current?.expeditor.apid);
        // if (expeditorIndex === ($count(workedSessionRef.current?.signatures)-1)) {
        //     const expeditorSignature = workedSessionRef.current?.signatures![expeditorIndex];
        //     if (expeditorSignature?.signatureType === SignatureType.AnyQSCD) {
        //         expeditorSignature!.phase = SignaturePhase.QSCDEnd;
        //     }
        // }

        optlog("workedSessionRef.current?.documents => ", workedSessionRef.current?.documents);
        const newSession = await updateOrCreateDraftSession(workedSessionRef.current!);
        if (!$ok(newSession?.apid)) { throw 'Bad save session' ; }
        return newSession;
    }

    const publishSessionForSignature = async () => {
        if (optionAddApproversSwitchChecked && $count(workedSessionRef.current?.approvals) === 0) {
            toast(defaultErrorToast(localtrans("NewSessionView.PleaseSelectAtLeastOneApprover")));
            return;
        }

        setModalText('Mise en signature ...') ;
        onOpen() ;
        let saved:boolean = false;
        try {
            const newSession = await _validateAndSendSessionData(true);
            saved = true ;
            setWorkedSession(newSession!);
            clearRightPane() ;
            if (!await publishSession(newSession!.apid!)) { throw 'Cannot publish' ; }

            let navigatePoint = 'tobeSignedDocuments';
            if ($count(newSession!.approvals) > 0) {
                navigatePoint = 'tobeApprovedDocuments';
            } else if (newSession?.signatures?.first()?.signer.apid === connectedUser!.apid) {
                navigatePoint = 'signatures'
            }
            toast(defaultSuccessToast("Mise en signature effectuée")) ;
            onClose() ;
            navigate(`/app/${navigatePoint}`, { replace: true });
        } catch (error) {
            var message: string;
            if (error instanceof TSError) {
                message = error.message;
            } else {
                message = saved ? "Impossible de mettre cette session en signature" : "Problème rencontré lors de l'enregistrement";
            }
            toast(defaultErrorToast(message));
            onClose() ;
        }
    }

    const saveSessionAsDraft = async () => {
        setModalText('Sauvegarde en cours ...') ;
        onOpen() ;
        try {
            const update = $ok(workedSessionRef.current?.apid);
            const newSession = await _validateAndSendSessionData(false);
            setWorkedSession(newSession!);
            clearRightPane();
            toast(defaultSuccessToast(update ? "Brouillon sauvegardé" : "Ajouté aux brouillons."));
        } catch (error) {
            toast(defaultErrorToast("Problème rencontré lors de l'enregistrement."));
        }
        onClose();
    }

    const saveSessionAsTemplate = async () => {
        setModalText('Sauvegarde en cours ...') ;
        onOpen() ;
        try {
            const update = $ok(workedSession?.apid) ;
            if (!optionAddApproversSwitchChecked && $count(workedSessionRef.current?.approvals) !== 0) { // Possible if user activate this option, select one or more approvers and deactivate the option
                workedSessionRef.current!.approvals = [];
            }
            workedSessionRef.current!.systemType = optionOneToManyModeSwitchChecked ? SessionSystemType.OneToManyTemplate : SessionSystemType.Template;
            const newSession = await updateOrCreateTemplateSession(workedSession!);
            if (!$ok(newSession?.apid)) { throw 'Bad save session' ; }
            setWorkedSession(newSession) ;    
            clearRightPane() ;
            toast(defaultSuccessToast(update ? "Modèle mis à jour" : "Ajouté à la liste de vos modèles."));
        } catch (error) {
            toast(defaultErrorToast("Problème rencontré lors de l'enregistrement."));
        }
        onClose();
    }

    // ==================== session card sender and signer management ========================================================================================
    
    const handleAddPersonAs = (role: NeededPersonRole) => {
        setPersonRole(role);
        setCanAddConnectedUser(_calculateCanAddMe(connectedUser?.apid, workedSession, role));
        setDocument(undefined);
        setPdfSignatures([]);
        setDrive(undefined);
        setSignature(undefined);
        if (role === NeededPersonRole.Sender) {
            setBlacklist(undefined);
        } else if (role === NeededPersonRole.Signer) {
            setBlacklist(workedSession?.signatures?.filteredSet(sign => sign.signer.apid));
            if ($count(workedSessionRef.current?.signatures) === config.app.nbMaxSigners) {
                toast(defaultWarningToast(localformat("NewSessionView.SignersLimitReached", { max: config.app.nbMaxSigners })))
                return;
            }
        } else if (role === NeededPersonRole.Approver) {
            setBlacklist(workedSession?.approvals?.filteredSet(a => a.approver.apid));
            if ($count(workedSessionRef.current?.approvals) === config.app.nbMaxApprovers) {
                toast(defaultWarningToast(localformat("NewSessionView.ApproversLimitReached", { max: config.app.nbMaxApprovers })))
                return;
            }
        }

        setRightPaneContent(RightPanelType.PersonSelection);
    }

    const handleRemoveUser = (apid: UUID, role: NeededPersonRole) => {
        if (role === NeededPersonRole.Signer) {
            if ($count(workedSession?.signatures) > 0) {
                const signerToRemove = workedSession!.signatures?.find(sign => sign.signer.apid === apid);
                if (!$ok(signerToRemove)) { return; }

                const newSignatures = $map(workedSession!.signatures!.filter(sign => sign.signer.apid !== apid), sign => optionOneToManyModeSwitchChecked || (sign.rank < signerToRemove!.rank) ? sign : {...sign, rank: sign.rank-1} as SignatureDto);
                const newSession: SessionDto = {
                    ...workedSession!,
                    signatures: newSignatures,
                };
                if (optionOneToManyModeSwitchChecked && $count(signaturesCache.current) !== 0) { // If user switch to solo mode after choosing signers in classic mode, then we must remove choosen signer to signatures cache
                    const index = signaturesCache.current!.findIndex(sign => sign.signer.apid === apid);
                    if (index !== -1) {
                        signaturesCache.current?.splice(index, 1);
                    }
                }

                setBlacklist(newSignatures.filteredSet(sg=>sg.signer.apid));
                setWorkedSession({ ...newSession }) ;
                if ($ok(signature)) { setSignature(undefined) ; }
                for (const docId of signersPositionMap.current.keys()) {
                    const docPositions = signersPositionMap.current.get(docId);
                    if (!$ok(docPositions)) { continue; }
                    const index = docPositions!.findIndex(pos => pos.identifier === apid)
                    if (index !== -1) { docPositions!.splice(index, 1); }
                }
                if (optionOneToManyModeSwitchChecked && $count(newSignatures) === 0) {
                    signersPositionMap.current.clear();
                }
                if ($ok(document)) { 
                    // FIXME: s.signatureDate is allways null or undefined here
                    setPdfSignatures(optionOneToManyModeSwitchChecked ? ($count(newSession?.signatures) === 0 ? [] : [virtualSignature]) : $map(newSession?.signatures, s => $ok(s.signatureDate) ? null : s)) ;
                    queryClient.invalidateQueries(DocumentContentID);
                }
            } else {
                toast(defaultErrorToast("Pas de signatures à supprimer")) ;
            }
        } else if (role === NeededPersonRole.Approver) {
            if ($count(workedSession?.approvals) === 0) { return; }

            const newSession = {...workedSession!};
            const index = newSession.approvals!.findIndex(a => a.approver.apid === apid);
            if (index !== -1) { newSession.approvals!.splice(index, 1); }
            setWorkedSession({...newSession});
            setBlacklist(newSession.approvals?.filteredSet(a => a.approver.apid));
        }
    }
    
    // ==================== right pane person management ========================================================================================
    // const _addNewPersonAsSigner = (personItem:PersonItem, phase:SignaturePhase) => {
    //     const newSignatures = _signaturesWithNewSigner(connectedUser?.apid, workedSession!, personItem, phase, signatureMode) ;
    //     if (typeof newSignatures === 'string') { toast(defaultErrorToast(newSignatures)) ; }
    //     else {
    //         const newSession = {...workedSession!, signatures:newSignatures } ;
    //         setWorkedSession(newSession) ;
    //         setCanAddConnectedUser(_calculateCanAddMe(connectedUser?.apid, newSession, NeededPersonRole.Signer)) ;
    //         setBlacklist(newSignatures.filteredSet(sg=>sg.signer.apid));
    //     }
    // }

    // const _chooseSignerPhase = (personItem: PersonItem) => {
    //     const message = [
    //         `Choisissez la qualité ${personItem.isConnectedUser?"de votre signature":"de la signature du signataire"} :`,
    //         localtrans('OTPSignatureOption'),
    //         localtrans('QSCDSignatureOption')
    //     ] ;

    //     setConfirmModal({
    //         title: localtrans('addSignerBoxTitle'),
    //         message: message.join(' '),
    //         cancel: localtrans('cancel'),
    //         confirm: localtrans('addSignerBoxQSCD'),
    //         onClose: async () => {
    //             setConfirmModal(DefaultAsyncConfirmModalState) ;
    //             clearRightPane() ;
    //         },
    //         onAccept: async () => {
    //             setConfirmModal(DefaultAsyncConfirmModalState) ;
    //             const qualifiedPhase = workedSession?.signatures?.last()?.phase === SignaturePhase.Server ? SignaturePhase.QSCDEnd : SignaturePhase.QSCDStart ;
    //             _addNewPersonAsSigner(personItem, qualifiedPhase) ;
    //             clearRightPane() ;
    //         },
    //         actions: [{
    //             value: undefined,
    //             label: localtrans('addSignerBoxStandardAction'),
    //             onSelect: async(_selectedValue: any) => {
    //                 setConfirmModal(DefaultAsyncConfirmModalState) ;
    //                 _addNewPersonAsSigner(personItem, SignaturePhase.Server) ; 
    //                 clearRightPane() ;
    //             }
    //         }]
    //     });            
    // }

    // const _chooseSenderPosition = (personItem: PersonItem, message: string[], first: boolean, last: boolean) => {
    //     const changeRank = (firstPosition: boolean) => {
    //         _changeRankOfOldSender(workedSession!);
    //         const signatures = _reloadSignatures(workedSession!, personItem, firstPosition);
    //         const newSession = { ...workedSession!, signatures:signatures, expeditor: personItem.person };
    //         setWorkedSession(newSession);
    //         clearRightPane();
    //     };

    //     if (optionOneToManyModeSwitchChecked) { // In OneToMany mode, signature has no order, so it's not necessary to have a confirmation from user
    //         if (first || last) { changeRank(first); }
    //         if (first && last) { changeRank(false); }
    //     } else {
    //         const modalState: AsyncConfirmModalState = {
    //             title: "Changer l'expéditeur ?",
    //             message: [
    //                 "Vous souhaitez changer l'expéditeur, cependant celui-ci est déjà présent dans la liste des signataires.",
    //                 ...message
    //             ].join(' '),
    //             cancel: localtrans('cancel'),
    //             onClose: async () => {
    //                 setConfirmModal(DefaultAsyncConfirmModalState);
    //                 clearRightPane();
    //             },
    //             onAccept: async () => {},
    //         };

    //         if (first || last) {
    //             modalState.confirm = first ? "En première position" : "En dernière position",
    //             modalState.onAccept = async () => {
    //                 setConfirmModal(DefaultAsyncConfirmModalState);
    //                 changeRank(first);
    //             }
    //         }

    //         if (first && last) {
    //             modalState.size = "xl";
    //             modalState.actions = [{
    //                 value: undefined,
    //                 label: "En dernière position",
    //                 onSelect: async(_selectedValue:any) => {
    //                     setConfirmModal(DefaultAsyncConfirmModalState);
    //                     changeRank(false);
    //                 }
    //             }];
    //         }

    //         setConfirmModal(modalState);
    //     }
    // }

    const _handlePersonAsSigner = async (personItem: PersonItem) => {
       const newSignatures = _signaturesWithNewSigner(connectedUser?.apid, workedSession!, personItem, /*phase,*/ signatureMode, optionOneToManyModeSwitchChecked);
        if (typeof newSignatures === 'string') { toast(defaultErrorToast(newSignatures)); }
        else {
            const newSession = {...workedSession!, signatures:newSignatures };
            setWorkedSession(newSession);
            setCanAddConnectedUser(_calculateCanAddMe(connectedUser?.apid, newSession, NeededPersonRole.Signer));
            setBlacklist(newSignatures.filteredSet(sg=>sg.signer.apid));
            if ($count(newSignatures) === config.app.nbMaxSigners) {
                clearRightPane();
            }
        }
        // if (optionOneToManyModeSwitchChecked) { // No mandatory checks when signing in OneToMany option, because each signature is standalone.
        //     _chooseSignerPhase(personItem) ;
        //     return;
        // }
 
        // const lastSignature = workedSession?.signatures?.last() ;
        // if ((signatureMode === SignatureMode.Invisible) /*|| (lastSignature?.phase === SignaturePhase.QSCDEnd)*/) {
        //     if ((signatureMode === SignatureMode.Visible) && showToastQualifiedSigner) { // Show this alert only when creator is in mode "Visible Signature", because, in other mode, we force qualified signature
        //         toast(defaultInfoToast(localtrans('NewSessionView.NextSignerQSCDModeMessage')));
        //         setShowToastQualifiedSigner(false);
        //     }
        //     _addNewPersonAsSigner(personItem, ((signatureMode === SignatureMode.Invisible) && $count(workedSession?.signatures) === 0) ? SignaturePhase.QSCDStart : workedSession!.signatures!.last()!.phase) ;
        //     clearRightPane() ;
        // } else if ((lastSignature?.signer.apid === workedSession?.expeditor.apid) && ($count(workedSession?.signatures) > 1)) {
        //     if (showToastSameAsExpeditor) {
        //         toast(defaultInfoToast(`Le dernier signataire étant l'expéditeur, les signataires suivants seront positionnés avant lui en tant que ${lastSignature?.signatureType === SignatureType.Connective ? localtrans('OTPSignature') : localtrans('QSCDSignature')}`)) ;
        //         setShowToastSameAsExpeditor(false) ;
        //     }
        //     _addNewPersonAsSigner(personItem, workedSession!.signatures!.last()!.phase) ;
        //     clearRightPane() ;
        // } else { // Server phase or first choice
        //     _chooseSignerPhase(personItem) ;
        // }
    }

    const _handlePersonAsApprover = async (personItem: PersonItem) => {
        const newSession = {...workedSession!};
        if (!$ok(newSession.approvals)) { newSession.approvals = []; }
        newSession.approvals?.push({ approver: personItem.person, rank: 0 as uint32 }); // Rank is not used here but mandatory in this structure
        clearRightPane();
        setWorkedSession(newSession);
    }

    const _handlePersonAsSender = async (personItem: PersonItem) => {
        const newSession = { ...workedSession!, expeditor: personItem.person } ;
        setWorkedSession(newSession) ;
        clearRightPane() ;

        // const indexOfNewExpeditor = workedSession?.signatures?.findIndex(signature => signature.signer.apid === personItem.person.apid) ;
        // const nbServerSignatures = workedSession?.signatures?.filter(signature => signature.phase === SignaturePhase.Server).length ;
        // const nbQscdStartSignatures = workedSession?.signatures?.filter(signature => signature.phase === SignaturePhase.QSCDStart).length ;

        // optlog(`handleSelectPersonAs => ${indexOfNewExpeditor} / ${nbServerSignatures} / ${nbQscdStartSignatures}`) ;

        // // If new expeditor is not in signature chain, so we can change it without asking connected user
        // // Don't forget to change the rank of old expeditor if he is in signature chain
        // if (!$ok(indexOfNewExpeditor) || (indexOfNewExpeditor === -1)) {
        //     _changeRankOfOldSender(workedSession!) ;
        //     const newSession = { ...workedSession!, expeditor: personItem.person } ;
        //     setWorkedSession(newSession) ;
        //     clearRightPane() ;

        // // If new expeditor is already in first or last position of signature chain, so only its rank must be changed
        // } else if ((indexOfNewExpeditor == 0) || (indexOfNewExpeditor == (workedSession!.signatures!.length - 1))) {
        //     _changeRankOfOldSender(workedSession!) ;
        //     const signatures = _reloadSignatures(workedSession!, personItem, indexOfNewExpeditor == 0) ;
        //     const newSession = { ...workedSession!, signatures: signatures, expeditor: personItem.person } ;
        //     setWorkedSession(newSession) ;
        //     clearRightPane() ;

        // // If all signatures are server OR all signatures are QSCDStart, then it's possible to move expeditor on first or last position
        // // Don't forget to change the rank of old expeditor if is in signature chain
        // } else if ((nbServerSignatures === workedSession?.signatures?.length) || (nbQscdStartSignatures === workedSession?.signatures?.length)) {
        //     const message = [
        //         "Pour rappel, un expéditeur ne peut être qu'en première ou dernière position de la chaine de signatures.",
        //         "A quelle position, voulez-vous le déplacer ?"
        //     ] ;
        //     _chooseSenderPosition(personItem, message, true, true) ;

        // // If new expeditor is in Advanded signature phase, so we ask user to move him at first position, because, currently,
        // // Advanced signature phase is before Qualified signature phase
        // // Don't forget to change the rank of old expeditor if is in signature chain
        // } else if (workedSession?.signatures![indexOfNewExpeditor!].phase === SignaturePhase.Server) {
        //     const message = [
        //         localtrans('OTPSignerForcedPosition'),
        //         "Voulez-vous la déplacer à cette position ?"
        //     ] ;
        //     _chooseSenderPosition(personItem, message, true, false) ;

        // // If new expeditor is in Qualified signature phase, so we ask user to move him at last position, because, currently,
        // // Qualified signature phase is after Advanced signature phase
        // // Don't forget to change the rank of old expeditor if is in signature chain
        // } else if (workedSession?.signatures![indexOfNewExpeditor!].phase === SignaturePhase.QSCDEnd) {
        //     const message = [
        //         localtrans('QSCDSignerForcedPosition'),
        //         "Voulez-vous la déplacer à cette position ?"
        //     ] ;
        //     _chooseSenderPosition(personItem, message, false, true) ;
        // }
    }

    const handleSelectPersonAs = async (personItem: PersonItem, role: NeededPersonRole) => {
        if (!$ok(workedSession)) {
            toast(defaultErrorToast("*** SESSION NOT IN PLACE ***")) ;
            return ;
        }

        if (role === NeededPersonRole.Signer) {
            _handlePersonAsSigner(personItem);
        } else if (role === NeededPersonRole.Approver) {
            _handlePersonAsApprover(personItem);
        } else if (personItem.person.apid !== workedSession!.expeditor.apid) {
            _handlePersonAsSender(personItem);
        } else { toast(defaultWarningToast("Vous avez choisi le même expéditeur")) ; }
    }

    // ==================== session card drive selection management ========================================================================================
    const handleSelectDrive = (drive: CloudSubscriptionDto) => {
        setRightPaneContent(RightPanelType.Drive);
        setDrive(drive) ;
        setPersonRole(undefined) ;
        setBlacklist(undefined) ; 
        setCanAddConnectedUser(false) ;
        setPdfSignatures([]) ;
        setDocument(undefined) ;
        setSignature(undefined) ;
    }

    // ==================== right pane drive management ========================================================================================
    const handleSelectFileFromDrive = async (drive: CloudSubscriptionDto, file: DriveItemStructure) => {
        if ($ok(workedSession)) {
            if ($count(workedSession?.documents) >= config.session.maxNbDoc) {
                toast(defaultWarningToast(`Vous avez atteint le nombre maximal de documents pour un dossier de signature.`)) ;
                return ;
            }

            if ($ok(file.size) && (file.size! > config.session.maxDocSize)) {
                toast(defaultErrorToast(`Le fichier ${file.name} dépasse les ${config.session.maxDocSize/(1024*1024)}Mo. Il ne peut pas être sélectionné.`)) ;
                return ;
            }

            if ($ok(file.size) && ((uploadedDocumentsSize + file.size!/*_realDocSize(file.size! as uint32)*/) > config.session.maxSessionSize)) {
                toast(defaultErrorToast(`La taille maximale du dossier a été dépassée. Vous ne pouvez pas sélectionner le fichier ${file.name}`)) ;
                return ;
            }

            const normalizedFilename = fileNameString(file.name) ;
            optlog(`select file ${file.name} ${normalizedFilename !== file.name ? ('(Normalisé : ' + normalizedFilename + ')') : ''} from drive ${drive.supplier}`) ;
            setModalText('Upload en cours...') ;
            onOpen() ;

            const filteredDoc = workedSession?.documents?.filter(doc => (file.mimeType === 'application/pdf' ? fileNameString(doc.fileName.toLowerCase()) : fileNameString($withoutext(doc.fileName.toLowerCase()))) === normalizedFilename.toLowerCase());
            if ($ok(filteredDoc) && (filteredDoc!.length > 0)) {
                toast(defaultWarningToast(`Un document portant un nom semblable à '${file.name}' a déjà été uploadé`));
            } else {
                const fileData = await downloadDriveFile(connectedUser?.apid, drive.apid, file.id) ;
                const newDocuments = $count(workedSession?.documents) ? [...workedSession!.documents!] : [] ;
                newDocuments.push({
                    fileName: file.name,
                    fileType:BCADocumentFileType.PDF,
                    mimeType:file.mimeType,
                    size:file.size,
                    pagesCount: 0 as uint32,
                    localUUID: $uuid(),
                    localSource: await blobToURLData(new Blob([$arrayBufferFromDataLike(fileData)], { type: file.mimeType })),
                    lastPageSignRows:3 as uint8,
                } as unknown as NewDocumentDto) ;
                const newSession = {
                    ...workedSession!,
                    documents: newDocuments,
                };
                setWorkedSession(newSession) ;
                setUploadedDocumentsSize((uploadedDocumentsSize + file.size!) as uint32) ;
                _handleViewDocument(newDocuments[newDocuments.length-1]) ;
                onClose() ;
            }
        } else {
            toast(defaultErrorToast("Impossible d'uoploader un nouveau document: session courante introuvable.")) ;
        }
    }

    // ==================== session card documents management ========================================================================================
    const handleUploadDocuments = async (files: FileList|BCADocumentFile[]|string) => {
        if ($isstring(files)) {
            toast(defaultWarningToast(files as string)) ;
        } else if ($ok(workedSession)) {
            const n = (files instanceof FileList) ? (files as FileList).length : $count(files as BCADocumentFile[]) ;
            if (n === 0) {
                toast(defaultWarningToast("Aucun document sélectionné.")) ;
                return ;
            }
            if (($count(workedSession?.documents) + n) > config.session.maxNbDoc) { 
                toast(defaultWarningToast(`Vous avez sélectionné trop de documents. Pour rappel, un dossier de signature ne peut pas en contenir plus de ${config.session.maxNbDoc}.`)) ;
                return ;
            }

            const errors: ToastInfo[] = [];
            let documents: BCADocumentFile[];
            if (files instanceof FileList) {
                documents = [];
                for(const f of files) {
                    try {
                        documents.push(new BCABrowserDocumentFile(f));
                    } catch (e) {
                        errors.push({ type: "warning", "text": `Le document ${f.name} n'est pas du bon type`});
                    }
                }
            } else {
                documents = files as BCADocumentFile[];
            }

            if ($count(errors) > 0) { 
                errors.forEach(m => toast(toastInfo(m)));
                if ($count(errors) === files.length) { return; }
            }

            setModalText('Upload en cours...') ;
            onOpen() ;
            const normalizedFilenames = $ok(workedSession!.documents) ? workedSession!.documents!.map(doc => { return fileNameString(doc.fileName) }) : [];
            const [newDocuments, messages] = await _uploadDocuments(documents as BCADocumentFile[], normalizedFilenames, uploadedDocumentsSize) ;
            messages.forEach(m => toast(toastInfo(m))) ;
            if (newDocuments.length > 0) {
                const newSession = {
                    ...workedSession!,
                    documents: $count(workedSession!.documents) > 0 ? [...workedSession!.documents!, ...newDocuments] : newDocuments
                };
                setWorkedSession(newSession) ;
                let uploadedNewDocumentsSize: uint32 = 0 as uint32;
                for (let document of newDocuments) {
                    uploadedNewDocumentsSize = (uploadedNewDocumentsSize + document.size/*_realDocSize(document.size! as uint32)*/) as uint32 ;
                }
                setUploadedDocumentsSize((uploadedDocumentsSize + uploadedNewDocumentsSize) as uint32) ;
                _handleViewDocument(newDocuments[0]) ;
            }
            onClose() ;
        } else {
            toast(defaultErrorToast("Impossible d'uploader de nouveaux documents : session courante introuvable.")) ;
        }
    }

    const _handleViewDocument = (visibleDocument:NewDocumentDto) => {
        optlog("count visible signatures dans document", $count(visibleDocument.visibleSignatures));
        if ($count(visibleDocument.visibleSignatures) === 0) { // Viewer based on visibleSignatures, so a new document uploaded has no visibleSignatures in DTO but has potential placed signers
            const docId = visibleDocument.apid ?? visibleDocument.localUUID;
            const docPositions = signersPositionMap.current.get(docId!);
            optlog("already positions", docPositions);
            if ($count(docPositions) > 0) {
                visibleDocument.visibleSignatures = docPositions;
                visibleDocument.pagesCount = (Math.max(...docPositions!.map(pos=> pos.page)) + 1) as uint32; // Force pageCount from docPositions (optional added page in memory) here because back stores only the real document page count
            }
        } else {
            visibleDocument.pagesCount = (Math.max(...visibleDocument.visibleSignatures!.map(pos=> pos.page)) + 1) as uint32; // Force pageCount from visibleSignatures (optional added page in memory) here because back stores the real document page count
        }
        setDocument(visibleDocument) ;

        setPdfSignatures(optionOneToManyModeSwitchChecked ? ($count(workedSession?.signatures) === 0 ? [] : [virtualSignature]) : $map(workedSession?.signatures, s => $ok(s.signatureDate) ? null : s)) ;
        setRightPaneContent(RightPanelType.Document);
        setCanAddConnectedUser(false) ;
        setPersonRole(undefined) ;
        setDrive(undefined) ;
        setSignature(undefined) ;
    }

    const handleViewDocument = (documentID: UUID) => {
        if ($count(workedSession?.documents) > 0) {
            const visibleDocument = workedSession?.documents?.find(d => d.apid === documentID || d.localUUID === documentID ? d : null ) ;
            if ($ok(visibleDocument)) { _handleViewDocument(visibleDocument!) ; }
            else { toast(defaultErrorToast("Document à visualiser non trouvé.")) ; }
        }
        else { toast(defaultErrorToast("Pas de document à visualiser.")) ; }
    }

    // ==================== right pane documents management ========================================================================================
    const handleLoadingFailed = (type: LoadingFailedType) => {
        var message: string;
        if (type === 'ENCRYPTED') {
            message = localtrans('NewSessionView.LoadingEncryptedPDF');
        } else if (type === 'READONLY') {
            message = localtrans('NewSessionView.LoadingReadonlyPDF');
        } else {
            message = localtrans('NewSessionView.LoadingFailedPDF');
        }
        toast(defaultErrorToast(message));
        handleRemoveDocument();
    }

    const handleRemoveDocument = () => {
        if ($count(workedSession?.documents) > 0 && $ok(document)) {
            setWorkedSession({
                ...workedSession!,
                documents: workedSession!.documents?.filter((d) => d !== document),
            });
            const docId = document!.apid ?? document!.localUUID;
            signersPositionMap.current.delete(docId!);
            setRightPaneContent(undefined) ;    
            setDocument(undefined);
        }
        else { toast(defaultErrorToast("Il n'y a pas de document courant à supprimer.")) ; }
    }

    // const handleSignatureLinesInDocument = (lines:uint8) => {
    //     lines = $tounsigned(lines) ;
    //     optlog('try to set new line to', lines) ;
    //     if ($count(workedSession?.documents) > 0 && $ok(document)) {
    //         if (lines !== document?.lastPageSignRows) {
    //             let newDocument:NewDocumentDto|undefined = undefined ;
    //             let newDocuments = $map(workedSession?.documents, d => {
    //                 if (d === document) {
    //                     d = { ...d, lastPageSignRows:lines } ;
    //                     newDocument = d ;
    //                 }
    //                 return d ;
    //             })
    //             setWorkedSession({...workedSession!, documents:newDocuments}) ;
    //             setDocument(newDocument) ;
    //         }
    //     }
    //     else { toast(defaultErrorToast("Document courant introuvable. Impossible de changer le nombre de ligne de signatures sur la dernière page.")) ; }
    // }

    const handleSaveSignerPosition = (docId: UUID, docPosition: DocumentSignaturePosition) => {
        optlog("handleSaveSignerPosition", docId, docPosition);
        const docPositions = signersPositionMap.current.get(docId);
        if (!$ok(docPositions)) { // New signer
            optlog("j'ajoute");
            signersPositionMap.current.set(docId, [docPosition]);
        } else {
            optlog("je mets à jour");
            const index = docPositions!.findIndex(pos => pos.identifier === docPosition.identifier);
            if (index !== -1) { docPositions!.splice(index, 1); }  // If it's an update position, then remove the old before insert the updated pos
            docPositions!.push(docPosition);
        }
    }

    const handleRemoveSignerPosition = (docId: UUID, signatureId: UUID) => {
        optlog("handleRemoveSignerPosition", docId, signatureId);
        const docPositions = signersPositionMap.current.get(docId);
        if ($ok(docPositions)) {
            const index = docPositions!.findIndex(pos => pos.identifier === signatureId);
            if (index !== -1) {
                optlog("je supprime");
                docPositions!.splice(index, 1);
            }
        }
    }

    const handleChangeSession = (_:SessionDto, newSession:SessionDto) => {
        setWorkedSession(newSession);
    }

    const handleToggleInvisibleSwitch = () => {
        // // If creator is in "Visible Signature" mode and clicks to "Invisible Signature" mode, and if advanced signatures are defined, so the signer list is reinitialized
        // if (signatureMode === SignatureMode.Visible && ($count(workedSession?.signatures?.filter(signature => signature.phase === SignaturePhase.Server)) !== 0)) {
        //     setConfirmModal({
        //         title: "Confirmation",
        //         message: localtrans('NewSessionView.ToggleSignatureVisibilitySwitchMessage'),
        //         confirm: "Convertir",
        //         cancel:localtrans('cancel'),
        //         actions: [{
        //             label: localtrans('delete'),
        //             value: undefined,
        //             onSelect: async (_selectedValue: any) => {
        //                 const qscdSignatures = workedSession!.signatures?.filter(signature => signature.signatureType ===  SignatureType.AnyQSCD);
        //                 const newSession = { ...workedSession!, signatures: qscdSignatures };
        //                 setWorkedSession(newSession);
        //                 setSignatureMode(SignatureMode.Invisible);
        //                 setOptionInvisibleSwitchChecked(true);
        //                 if ($ok(document)) { setDocument(undefined); clearRightPane(); }
        //                 setConfirmModal(DefaultAsyncConfirmModalState);
        //             }
        //         }],
        //         onAccept: async () => {
        //             for (let signature of workedSession!.signatures!) {
        //                 signature.signatureType = SignatureType.AnyQSCD ;
        //                 signature.phase = SignaturePhase.QSCDStart ;
        //             } ;
        //             const newSession = { ...workedSession! } ;
        //             setWorkedSession(newSession) ;
        //             setSignatureMode(SignatureMode.Invisible) ;
        //             setOptionInvisibleSwitchChecked(true) ;
        //             if ($ok(document)) { setDocument(undefined); clearRightPane(); }
        //             setConfirmModal(DefaultAsyncConfirmModalState) ;
        //         },
        //         onClose: async () => {
        //             setOptionInvisibleSwitchChecked(false) ;
        //             setConfirmModal(DefaultAsyncConfirmModalState) ;
        //         }
        //     });
        // } else {
            const mode = signatureMode === SignatureMode.Visible ? SignatureMode.Invisible : SignatureMode.Visible;
            setSignatureMode(mode);
            setOptionInvisibleSwitchChecked(!optionInvisibleSwitchChecked) ;
            const newSession = { ...workedSessionRef.current!, signatures: $map(workedSessionRef.current?.signatures, sign => { return { ...sign, signatureMode: mode } as SignatureDto }) };
            setWorkedSession(newSession);
            if ($ok(document)) { setDocument(undefined); clearRightPane(); }
        // }
    }

    const handleToggleAddApproversSwitch = () => {
        const newOption = !optionAddApproversSwitchChecked;
        setOptionAddApproversSwitchChecked(newOption);
    }

    const handleToggleOneToManyModeSwitch = () => {
        if ($ok(document)) { setDocument(undefined); clearRightPane(); }

        const newOption = !optionOneToManyModeSwitchChecked;
        setOptionsOneToManyModeSwitchChecked(newOption);
        if (newOption) {
            signaturesCache.current = workedSessionRef.current?.signatures;
            const newSession = { ...workedSessionRef.current!, signatures: $map(workedSessionRef.current?.signatures, sign => { return { ...sign, rank: 0 as uint32 } as SignatureDto }) };
            setWorkedSession(newSession);
        } else if ($count(signaturesCache.current) !== 0) {
            const newSession = { ...workedSessionRef.current!, signatures:signaturesCache.current! };
            setWorkedSession(newSession);
        }
        // if (!newOption) {
        //     const totalCountSignatures = $count(workedSessionRef.current?.signatures);
        //     const totalServerSignatures = $count(workedSessionRef.current?.signatures?.filter(s => s.phase === SignaturePhase.Server));
        //     const totalQSCDSignatures = $count(workedSessionRef.current?.signatures?.filter(s => [SignaturePhase.QSCDStart, SignaturePhase.QSCDEnd].includes(s.phase)));
        //     if (totalCountSignatures === totalServerSignatures || totalCountSignatures === totalQSCDSignatures) { return; } // Only one signature phase type found, so no problem
            
        //     // Mix of server and qscd (start or end) signatures found
        //     const firstIndexOfQSCDEndPhase = workedSessionRef.current!.signatures!.findIndex(s => s.phase === SignaturePhase.QSCDEnd);
        //     if (firstIndexOfQSCDEndPhase === -1) { return; } // No QSCD phase after server phase found, so no problem

        //     const lastIndexOfServerPhase = workedSessionRef.current!.signatures!.findLastIndex(s => s.phase === SignaturePhase.Server);
        //     if (lastIndexOfServerPhase < firstIndexOfQSCDEndPhase) { return; } // No server phase after qscd end phase found, so no problem

        //     const serverSignaturesToUpdate = workedSessionRef.current!.signatures!.slice(firstIndexOfQSCDEndPhase).filter(s => s.phase === SignaturePhase.Server);
        //     const serverSignatureIdsToUpdate = new Set(serverSignaturesToUpdate.map(s => s.signer.apid!));
        //     setConfirmModal({
        //         title: "Confirmation",
        //         message: localformat('NewSessionView.ToggleOneToManySwitchMessage', { firstSigner: `${serverSignaturesToUpdate[0].signer.firstName} ${serverSignaturesToUpdate[0].signer.lastName}`}),
        //         confirm: "Convertir",
        //         cancel: localtrans('cancel'),
        //         actions: [{
        //             label: localtrans('delete'),
        //             value: undefined,
        //             onSelect: async (_selectedValue: any) => {
        //                 const filteredSignatures = workedSessionRef.current!.signatures!.filter(signature => !serverSignatureIdsToUpdate.has(signature.signer.apid!));
        //                 const newSession = { ...workedSessionRef.current!, signatures: filteredSignatures };
        //                 setWorkedSession(newSession);
        //                 setConfirmModal(DefaultAsyncConfirmModalState);
        //             }
        //         }],
        //         onAccept: async () => {
        //             const filteredSignatures = workedSessionRef.current!.signatures!.filter(signature => serverSignatureIdsToUpdate.has(signature.signer.apid!));
        //             for (let signature of filteredSignatures) {
        //                 signature.signatureType = SignatureType.AnyQSCD ;
        //                 signature.phase = SignaturePhase.QSCDEnd ;
        //             };
        //             const newSession = { ...workedSessionRef.current! } ;
        //             setWorkedSession(newSession) ;
        //             setConfirmModal(DefaultAsyncConfirmModalState) ;
        //         },
        //         onClose: async () => {
        //             setConfirmModal(DefaultAsyncConfirmModalState) ;
        //         }
        //     });
        // }
    }

    useEffect(() => {
        workedSessionRef.current = workedSession!;
    }, [workedSession]);

    useEffect(() => {
        if (initialSession) {
            const now = TSDate.zulu() ;
            let workedSession = initialSession;
            if (systemType === SessionSystemType.Template) {
                if ($ok(initialSession.systemType) && [SessionSystemType.Standard, SessionSystemType.OneToMany].includes(initialSession.systemType!)) { // Create template from session
                    workedSession = {
                        title: '',
                        status: SessionStatus.Draft,
                        initialCreator: initialSession.initialCreator,
                        creator: initialSession.creator,
                        expeditor: initialSession.expeditor,
                        validity: new TSInterval(now, now.dateByAddingDays(14)),
                        currentPhase: SignaturePhase.None,
                        signatures: initialSession.signatures,
                        approvals: initialSession.approvals,
                        systemType: initialSession.systemType === SessionSystemType.Standard ? SessionSystemType.Template : SessionSystemType.OneToManyTemplate
                    } ;
                }
            } else {
                if ($ok(initialSession.systemType) && [SessionSystemType.Template, SessionSystemType.OneToManyTemplate].includes(initialSession.systemType!)) { // Create session from template
                    workedSession = {
                        title: "",
                        status: initialSession.status,
                        initialCreator: initialSession.initialCreator,
                        creator: initialSession.creator,
                        expeditor: initialSession.expeditor,
                        validity: new TSInterval(now, now.dateByAddingDays($timeBetweenDates(initialSession.validity.start, initialSession.validity.end) / TSDay)),
                        currentPhase: SignaturePhase.None, //initialSession.currentPhase,
                        signatures: initialSession.signatures,
                        approvals: initialSession.approvals,
                        templateId: initialSession.apid,
                        systemType: initialSession.systemType === SessionSystemType.Template ? SessionSystemType.Standard : SessionSystemType.OneToMany
                    } ;
                }
            }
            setWorkedSession(workedSession) ;
            setSignatureMode(initialSession!.signatures?.first()?.signatureMode || SignatureMode.Visible) ;
            setOptionInvisibleSwitchChecked(initialSession!.signatures?.first()?.signatureMode === SignatureMode.Invisible) ;
            setOptionAddApproversSwitchChecked($count(initialSession.approvals) !== 0);
            setOptionsOneToManyModeSwitchChecked(workedSession.systemType === SessionSystemType.OneToMany || workedSession.systemType === SessionSystemType.OneToManyTemplate);
            setBlacklist(undefined);
            setDocument(undefined);
            if ($count(workedSession.documents) > 0) {
                const docWithVisibleSignatures = workedSession.documents!.filter(doc => $ok(doc.visibleSignatures));
                for (const document of docWithVisibleSignatures) {
                    signersPositionMap.current.set(document.apid!, document.visibleSignatures!);
                }
            }

            setDrive(undefined) ;
            setPersonRole(undefined) ;
            setRightPaneContent(undefined) ;
            setCanAddConnectedUser(false) ;
            setPdfSignatures([]) ;
        }
    }, [initialSession]);

    return trueOrEmptyCard(
        isUserAuthenticated,
        <>
            <PageShellWithThreeColumns
                centralContent={
                    okOrEmptyCard(workedSession,
                        systemType === SessionSystemType.Template ? 
                            (<TemplateSessionCreationPanel
                                session={workedSession!}
                                userProfile={connectedUser!}
                                optionAddApproversSwitchChecked={optionAddApproversSwitchChecked}
                                optionInvisibleSwitchChecked={optionInvisibleSwitchChecked}
                                optionOneToManyModeSwitchChecked={optionOneToManyModeSwitchChecked}
                                onAddUser={handleAddPersonAs}
                                onRemoveUser={handleRemoveUser}
                                onChangeSession={handleChangeSession}
                                onSaveTemplate={saveSessionAsTemplate}
                                onToggleAddApproversSwitch={handleToggleAddApproversSwitch}
                                onToggleInvisibleSwitch={handleToggleInvisibleSwitch}
                                onToggleOneToManyModeSwitch={handleToggleOneToManyModeSwitch}
                            />)
                            : (<SessionCreationPanel
                                    session={workedSession!}
                                    userProfile={connectedUser!}
                                    officeDocuments={config.app.officeDocuments}
                                    openFailModal={openFailModal}
                                    optionAddApproversSwitchChecked={optionAddApproversSwitchChecked}
                                    optionInvisibleSwitchChecked={optionInvisibleSwitchChecked}
                                    optionOneToManyModeSwitchChecked={optionOneToManyModeSwitchChecked}
                                    onAddUser={handleAddPersonAs}
                                    onChangeSession={handleChangeSession}
                                    onPutToSignature={publishSessionForSignature}
                                    onRemoveUser={handleRemoveUser}
                                    onSaveAsDraft={saveSessionAsDraft}
                                    onSelectDrive={handleSelectDrive}
                                    onToggleAddApproversSwitch={handleToggleAddApproversSwitch}
                                    onToggleInvisibleSwitch={handleToggleInvisibleSwitch}
                                    onToggleOneToManyModeSwitch={handleToggleOneToManyModeSwitch}
                                    onUploadFiles={handleUploadDocuments}
                                    onViewDocument={handleViewDocument}
                                    setOpenFailModal={setOpenFailModal}
                            />),
                        isLoading,
                        isError)
                }
                rightContent={
                    <>
                        {rightPaneContent === RightPanelType.PersonSelection && (
                            <PersonPanel profileId={connectedUser?.apid!} blacklist={blacklist} role={personRole!} canaddme={canAddConnectedUser} connectedUserProfile={connectedUser!} enableAddUserTab={personRole !== NeededPersonRole.Approver} enableAllUsersTab={personRole !== NeededPersonRole.Approver}
                                         onSelectPersonItemAs={handleSelectPersonAs} onBack={clearRightPane} />
                        )}
                        {rightPaneContent === RightPanelType.Document && (
                            <DocumentPanel viewerRef={viewerRef} document={document!} message={document?.conversionWarning} signatures={pdfSignatures} signatureMode={signatureMode}
                                           onBack={clearRightPane} onRemoveDocument={handleRemoveDocument} /*onChangeSignatureLines={handleSignatureLinesInDocument}*/ onSaveSignerPosition={handleSaveSignerPosition} onRemoveSignerPosition={handleRemoveSignerPosition} onLoadingFailed={handleLoadingFailed} />
                        )}
                        {rightPaneContent === RightPanelType.Drive && (
                            <DriveExplorerPanel drive={drive!} onBack={clearRightPane} onSelectFile={handleSelectFileFromDrive} />
                        )}
                    </>
                }
                isOpen={rightPaneContent !== undefined}
            />
{
//            <AsyncConfirmModal title={confirmModal.title} message={confirmModal.message} size={confirmModal.size} onClose={confirmModal.onClose} onAccept={confirmModal.onAccept} actions={confirmModal.actions} confirm={confirmModal.confirm} cancel={confirmModal.cancel} />
}
            <WaitingModal isOpen={isOpen} onClose={onClose} message={modalText} />
        </>,
        false,
        !isUserAuthenticated,
        "Impossible de récupérer le profil de l'utilisateur",
        "Connexion invalide",
        "Chargement du profil de l'utilisateur"
    ) ;
};

enum RightPanelType {
    Document,
    Drive,
    PersonSelection
}

interface NewDocumentDto extends DocumentDto {
    conversionWarning?:string ;
}

const virtualSignature: SignatureDto = {
    phase: SignaturePhase.None,
    rank: 1 as uint32,
    signatureStatus: SignatureStatus.ToBeOpened,
    signatureType: SignatureType.Connective,
    signer: {
        apid: $UUID("aaaaaaaa-0000-1234-5678-0000bbbbbbbb")!,
        firstName: "Emplacement",
        lastName: "SIGNATAIRE",
        email: $email("email@example.com")!
    }
};

interface CheckedFile {
    urlData?: string;
    filename?: string;
    warning?: string;
    size?: number;
}

async function _uploadDocuments(files:BCADocumentFile[], filenames: string[], uploadedDocumentsSize: uint32):Promise<[NewDocumentDto[], ToastInfo[]]> {
    const ret:NewDocumentDto[] = [];
    const messages:ToastInfo[] = [];
    const n = files.length;

    for (let i = 0 ; i < n ; i++) {
        const doc = files[i];
        let size = await doc.getSize() ;
        if (size === 0 || $length(doc?.name) === 0) {
            messages.push({ type:'warning', text:`Le ${i===0?"1er":""+(i+1)+"e"} document n'est pas uploadable.`}) ;
            continue;
        }

        if (size > config.session.maxDocSize) { 
            messages.push({ type: 'warning', text: `Le document ${doc.fileName} dépasse les ${config.session.maxDocSize/(1024*1024)}Mo. Il ne peut pas être sélectionné.` }) ;
            continue ;
        }

        const filteredDoc = filenames.filter(name => (doc.type === BCADocumentFileType.PDF ? name.toLowerCase() : $withoutext(name.toLowerCase())) === fileNameString(doc.fileName).toLowerCase()) ;
        if (filteredDoc.length > 0) {
            messages.push({ type: 'warning', text: `Un document portant un nom semblable à '${doc.fileName}' a déjà été uploadé` }) ;
            continue ;
        }

        let checkedFile: CheckedFile = {};
        try {
            if (doc.type === BCADocumentFileType.PDF) {
                checkedFile = await _checkPDFDocument(doc, messages);
            } else if (config.app.officeDocuments) {
                checkedFile = await _convertToPDF(doc, messages);
            } else {
                messages.push({ type:'warning', text:`Le ${i===0 ? "1er" : ""+(i+1)+"e"} document n'est pas du bon type.`}) ;
            }
        } catch (e) {
            messages.push({ type: "warning", text: "Impossible de vérifier les permissions du document PDF ou de convertir le document en PDF" });
        }

        if ($length(checkedFile.urlData) > 0) {
            if (uploadedDocumentsSize + size > config.session.maxSessionSize) { // If we exceed the maximum size, we exit the loop
                messages.push({ type: 'warning', text: `Le document ${checkedFile.filename} ne sera pas pris en compte (sa taille faisant dépasser la taille maximale du dossier).` }) ;
                continue ;
            }

            ret.push({
                fileName: checkedFile.filename!,
                fileType: BCADocumentFileType.PDF,
                mimeType: "application/pdf",
                size: (checkedFile.size ?? size) as uint32,
                pagesCount: 0 as uint32,
                localUUID: $uuid(),
                localSource: checkedFile.urlData!,
                lastPageSignRows: 3 as uint8,
                conversionWarning: checkedFile.warning
            }) ;
        }
    }

    return [ret, messages] ;
}

/***/
async function _checkPDFDocument(doc: BCADocumentFile, messages: ToastInfo[]): Promise<CheckedFile> {
    let buf:Nullable<TSDataLike> = await doc.toDataLike() ;
    if ($length(buf) === 0) { 
        messages.push({ type:'warning', text:`Impossible de charger le document PDF ${doc.fileName}`}) ; 
        return {};
    }

    let filename = "" ;
    let urlData:Nullable<string> = null ;
    let exitCondition = 0;
    while (exitCondition <= 1) { // Two retries max (only for write-protected or readonly documents)
        const ret = await checkDocumentPermissions(buf!);
        switch (ret) {
            case BCAReturnCode.EncryptedDocument:
                exitCondition = 2;
                buf = null ;
                messages.push({ type: 'warning', text: `Le document PDF '${doc.fileName}' est protégé par mot de passe, il ne peut donc pas être signé.`});
                break;

            case BCAReturnCode.WriteProtecteddDocument:
            case BCAReturnCode.ReadonlyDocument:
                exitCondition++;
                buf = await g1call('unprotect PDF document', c => c.unprotectFile({ source: $encodeBase64(buf!), fileName: filename }));
                if (!$ok(buf)) {
                    messages.push({ type: 'warning', text: `Le document PDF '${doc.fileName}' est ${ret === BCAReturnCode.WriteProtecteddDocument ? 'protégé en écriture' : 'en lecture seule'}. La tentative d'enlever la protection a échoué, il ne peut donc pas être signé.`});
                    exitCondition = 2;
                }
                break;

            case BCAReturnCode.NoChangesPermitted:
                exitCondition = 2;
                buf = null ;
                messages.push({ type: 'warning', text: `Le document PDF '${doc.fileName}' est protégé de manière à ce qu'aucune signature ne puisse être ajoutée, il ne peut donc pas être signé à nouveau.`});
                break;

            default:
                break;
        }
        if (exitCondition === 2) { continue; }

        const checkDSSObjectsRet = await extractAndCheckDSSObject(buf!);
        if ($isnumber(checkDSSObjectsRet) && (checkDSSObjectsRet !== BCAReturnCode.NotSignedWithLTLevel)) {
            exitCondition = 2; 
            buf = null;
            messages.push({ type: 'warning', text: `Le document PDF '${doc.fileName}' est signé, mais le format des données signées est incorrect. Il ne peut donc pas être signé.`});
            continue;
        }

        exitCondition = 2;
        const dssObject = (checkDSSObjectsRet === BCAReturnCode.NotSignedWithLTLevel) ? null : checkDSSObjectsRet as BCAObjectDSS;
        const checkSignaturesRet = await extractAndVerifySignatures(buf!, true, dssObject);
        if ($isnumber(checkSignaturesRet) && (checkSignaturesRet !== BCAReturnCode.NoSignatureFound)) { 
            buf = null ;
            messages.push({ type: 'warning', text: `Une erreur s'est produite lors de la vérification des signatures du document PDF '${doc.fileName}'.`});
        } else if (!$isnumber(checkSignaturesRet) && ($count((checkSignaturesRet as BCAPDFSignedData[]).filter(sign => !sign.valid)) > 0)) {
                buf = null ;
                messages.push({ type:'warning', text:`Le document PDF '${doc.fileName}' est signé, mais certaines signatures sont considérées comme invalides. Il ne peut donc pas être signé à nouveau.`}) ;
        } else {
            urlData = `data:application/pdf;base64,${$encodeBase64(buf!)}`;
            filename = $newext(doc.fileName, 'pdf');
        }
    }
    return $ok(urlData) ? { urlData: urlData!, filename } : {};
}

/***/
async function _convertToPDF(doc: BCADocumentFile, messages: ToastInfo[]): Promise<CheckedFile> {
    let filename = "" ;
    let urlData:Nullable<string> = null ;
    let size = -1;
    let warning = "";

    const base64String = await doc.toBase64() ;
    if ($length(base64String) !== 0) {
        const conversion = await g1call('convert to pdf', c => c.convertToPDF({ source:base64String!, fileName:doc.fileName })) ;
        if ($ok(conversion)) {
            urlData = conversion!.urlData ; 
            size = conversion!.fileSize ;
            filename = `${doc.fileName}.pdf`;
            messages.push({ type:'success', text:`Le document ${doc.name} '${doc.fileName}' a été converti en PDF.`})
            warning = `Ce document PDF a été converti depuis le document ${doc.name} '${doc.fileName}'` ;
        } else {
            messages.push({ type:'warning', text:`Erreur à la conversion du document ${doc.name} '${doc.fileName}'`}) ;
        }
    } else {
        messages.push({ type:'warning', text:`Erreur à la lecture du document ${doc.name} '${doc.fileName}'`}) ;
    }

    return $ok(urlData) ? { urlData: urlData!, filename, warning, size } : {};
}

/***/
function _calculateCanAddMe(connectedUserID:Nullable<UUID>, session:Nullable<SessionDto>, role: NeededPersonRole): boolean {
    if (!$ok(connectedUserID) || !$ok(session)) { return false; }
    if (role === NeededPersonRole.Sender) {
        return connectedUserID! !== session!.expeditor.apid;
     } else if (role === NeededPersonRole.Approver) {
        return $value(session?.approvals, []).findIndex(a => a.approver.apid === connectedUserID) === -1;
     } else {
        return $value(session?.signatures, []).findIndex(s => s.signer.apid === connectedUserID) === -1;
     }
}

/***/
function _signaturesWithNewSigner(connectedUserID:Nullable<UUID>, session:SessionDto, personItem:PersonItem, /*phase:SignaturePhase,*/ mode: SignatureMode, soloMode: boolean): SignatureDto[]|string {
    optlog('session want to add signer', $inspect(personItem), session?.signatures?.length)
    if ($length(connectedUserID) === 0) { return "Connexion au serveur perdue" ; }
    if (!$ok(session)) { return "Session perdue" ; }

    const signatures = $ok(session.signatures) ? [...session.signatures!] : [] ;
    const personID = personItem.person.apid ;

    if ($ok(signatures.find(sign => sign.signer.apid === personID))) { return "Ce signataire déjà dans votre liste" ; } // the signer is already in our list

    const newSignature: SignatureDto = {
        signatureStatus: SignatureStatus.ToBeOpened,
        signatureType: SignatureType.UserChoice,
        signer: personItem.person,
        phase: SignaturePhase.Standard,
        signatureMode: mode,
        rank: ($count(signatures) === 0 || soloMode) ? 0 as uint32 : signatures.last()!.rank+1 as uint32
    } ;

    // if (personItem.isConnectedUser) {
    // if (personItem.person.apid === session.expeditor.apid) {
    //     newSignature.rank = (signatures.length === 0 ? 0 : 2) as uint32 ; 
    //     // if you add yourself first, 
    //     // we consider you want the connected user is maint to stay at the first position. 
    //     // If not, it stays at the end
    //     signatures.push(newSignature) ;
    // }
    // else if (signatures.length) {
    //     const index = signatures.findIndex((sign) => sign.signer.apid === session.expeditor.apid) ; // connectedUserID) ;
    //     signatures.push(newSignature) ;
    //     if (index > 0) {
    //         // we add the expeditor user at the end or in the middle of the list
    //         const expeditorUserSignature = {...signatures[index], rank:2 as uint32 }
    //         signatures.splice(index, 1) ;
    //         signatures.push(expeditorUserSignature) ;
    //     }
    // }
    // else { signatures.push(newSignature) ; }
    signatures.push(newSignature);
    optlog('signatures', $inspect(signatures)) ;
    return signatures ;
}

// function _reloadSignatures(session: SessionDto, expeditor: PersonItem, firstPosition: boolean): SignatureDto[] {
//     const signatures = $ok(session.signatures) ? [...session.signatures!] : [] ;
//     const index = signatures.findIndex(signature => signature.signer.apid === expeditor.person.apid) ;
//     if (index !== -1) {
//         const expeditorUserSignature = {...signatures[index], rank: (firstPosition ? 0 : 2) as uint32 } ;
//         signatures.splice(index, 1) ;
//         return firstPosition ? [expeditorUserSignature, ...signatures] : [...signatures, expeditorUserSignature] ;
//     }

//     return signatures ;
// }

// function _changeRankOfOldSender(session: SessionDto) {
//     const indexOfOldExpeditor = session.signatures?.findIndex(signature => signature.signer.apid === session!.expeditor.apid) ;
//     if ($ok(indexOfOldExpeditor) && (indexOfOldExpeditor !== -1)) {
//         session!.signatures![indexOfOldExpeditor as number].rank = 1 as uint32 ;
//     }
// }
// function _realDocSize(length: uint32): uint32 {
//     return ((length+63)*41/30) as uint32 ;
// }